import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import type { CartItem } from '../types';
import { useAuth } from './AuthContext';
import { getTierLimits } from '../data/tiers';

// Storage key prefix (user-specific)
const CART_STORAGE_PREFIX = 'brickmod-cart-';

// State interface
interface CartState {
  items: CartItem[];
  isOpen: boolean;
}

// Action types
type CartAction =
  | { type: 'ADD_ITEM'; payload: string }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_CART' }
  | { type: 'SET_OPEN'; payload: boolean }
  | { type: 'HYDRATE'; payload: CartItem[] };

// Initial state
const initialState: CartState = {
  items: [],
  isOpen: false,
};

// Reducer
function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      // Check if item already exists
      const exists = state.items.some((item) => item.setId === action.payload);
      if (exists) return state;

      const newItem: CartItem = {
        setId: action.payload,
        addedAt: new Date().toISOString(),
      };
      return {
        ...state,
        items: [...state.items, newItem],
      };
    }

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter((item) => item.setId !== action.payload),
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
      };

    case 'TOGGLE_CART':
      return {
        ...state,
        isOpen: !state.isOpen,
      };

    case 'SET_OPEN':
      return {
        ...state,
        isOpen: action.payload,
      };

    case 'HYDRATE':
      return {
        ...state,
        items: action.payload,
      };

    default:
      return state;
  }
}

// Context value interface
interface CartContextValue extends CartState {
  addItem: (setId: string) => boolean;
  removeItem: (setId: string) => void;
  clearCart: () => void;
  toggleCart: () => void;
  itemCount: number;
  canAddMore: boolean;
}

// Create context
const CartContext = createContext<CartContextValue | undefined>(undefined);

// Storage helper functions
function getCartStorageKey(userId: string): string {
  return `${CART_STORAGE_PREFIX}${userId}`;
}

function saveCartToStorage(userId: string, items: CartItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(getCartStorageKey(userId), JSON.stringify(items));
  } catch (error) {
    console.warn('Failed to save cart to localStorage:', error);
  }
}

function loadCartFromStorage(userId: string): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(getCartStorageKey(userId));
    if (stored) {
      return JSON.parse(stored) as CartItem[];
    }
  } catch (error) {
    console.warn('Failed to load cart from localStorage:', error);
  }
  return [];
}

function clearCartFromStorage(userId: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(getCartStorageKey(userId));
  } catch (error) {
    console.warn('Failed to clear cart from localStorage:', error);
  }
}

// Provider component
interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Get the tier limit for the current user
  const tierLimit = useMemo(() => {
    if (!user) return 0;
    return getTierLimits(user.tier);
  }, [user]);

  // Calculate derived values
  // For tier limits, we count both sets at home AND items in cart
  const itemCount = state.items.length;
  const setsAtHomeCount = user?.setsAtHome?.length ?? 0;
  const totalCommitted = itemCount + setsAtHomeCount;
  const canAddMore = totalCommitted < tierLimit;

  // Get user ID for storage operations
  const userId = user?.id;

  // Hydrate cart from localStorage when user changes
  useEffect(() => {
    if (isAuthenticated && userId) {
      const storedItems = loadCartFromStorage(userId);
      dispatch({ type: 'HYDRATE', payload: storedItems });
    } else {
      // Clear cart when user logs out
      dispatch({ type: 'HYDRATE', payload: [] });
    }
  }, [isAuthenticated, userId]);

  // Persist cart to localStorage when items change
  useEffect(() => {
    if (userId && isAuthenticated) {
      saveCartToStorage(userId, state.items);
    }
  }, [state.items, userId, isAuthenticated]);

  const addItem = useCallback(
    (setId: string): boolean => {
      if (!canAddMore) {
        return false;
      }
      // Check if already in cart
      if (state.items.some((item) => item.setId === setId)) {
        return false;
      }
      dispatch({ type: 'ADD_ITEM', payload: setId });
      return true;
    },
    [canAddMore, state.items]
  );

  const removeItem = useCallback((setId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: setId });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
    if (user) {
      clearCartFromStorage(user.id);
    }
  }, [user]);

  const toggleCart = useCallback(() => {
    dispatch({ type: 'TOGGLE_CART' });
  }, []);

  const value: CartContextValue = {
    ...state,
    addItem,
    removeItem,
    clearCart,
    toggleCart,
    itemCount,
    canAddMore,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// Custom hook
// eslint-disable-next-line react-refresh/only-export-components
export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export default CartContext;
